import * as THREE from "three";
import { noise3D } from "../ShaderFunctions.js";

/**
 * Base uniforms shared by all procedural shaders
 */
export const baseUniforms = {
  uTime: { value: 0.0 },
  uWorldScale: { value: 1.0 },
  uPixelSize: { value: 12.0 },
  uScale: { value: 1.0 },
  uSpeed: { value: 0.2 },
};

/**
 * Standard vertex shader for procedural wall shaders
 */
export const baseVertexShader = `
  varying vec2 vUv;
  varying vec3 vWorldPosition;

  void main() {
    vUv = uv;
    vWorldPosition = (modelMatrix * vec4(position, 1.0)).xyz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

/**
 * Common fragment shader header with noise functions and uniforms
 */
export const fragmentHeader = `
  ${noise3D}

  varying vec2 vUv;
  varying vec3 vWorldPosition;

  uniform float uTime;
  uniform float uWorldScale;
  uniform float uPixelSize;
  uniform float uScale;
  uniform float uSpeed;
`;

/**
 * Common fragment shader setup - scales and pixelates world position
 * Returns: scaledPos, pixelatedPos, animatedPos
 */
export const fragmentSetup = `
  // Scale world position to match player mode scale
  vec3 scaledPos = vWorldPosition * uWorldScale;

  // Pixelate the world position
  vec3 pixelatedPos = vec3(
    floor(scaledPos.x * uPixelSize) / uPixelSize,
    floor(scaledPos.y * uPixelSize) / uPixelSize,
    floor(scaledPos.z * uPixelSize) / uPixelSize
  );

  // Animate position
  vec3 animatedPos = pixelatedPos * uScale + vec3(
    uTime * uSpeed * 0.3,
    uTime * uSpeed * 0.2,
    uTime * uSpeed * 0.4
  );
`;

/**
 * Creates a procedural shader with common base functionality
 *
 * @param {Object} config - Shader configuration
 * @param {Array} config.customizable - Array of customizable uniform definitions for UI
 * @param {Object} config.uniforms - Custom uniforms (merged with base uniforms)
 * @param {string} config.fragmentUniforms - Additional uniform declarations for fragment shader
 * @param {string} config.fragmentBody - The main fragment shader body (color calculation)
 * @returns {Object} Complete shader object
 */
export function createShader({ customizable = [], uniforms = {}, fragmentUniforms = "", fragmentBody }) {
  // Merge base uniforms with custom uniforms
  const mergedUniforms = {
    ...Object.fromEntries(
      Object.entries(baseUniforms).map(([key, val]) => [key, { value: val.value }])
    ),
    ...uniforms,
  };

  return {
    customizable,
    uniforms: mergedUniforms,
    vertexShader: baseVertexShader,
    fragmentShader: `
      ${fragmentHeader}
      ${fragmentUniforms}

      void main() {
        ${fragmentSetup}
        ${fragmentBody}
      }
    `,
  };
}

/**
 * Helper to create a THREE.Color uniform
 */
export function colorUniform(hexOrRgb) {
  if (typeof hexOrRgb === "string") {
    return { value: new THREE.Color(hexOrRgb) };
  }
  return { value: new THREE.Color(hexOrRgb.r, hexOrRgb.g, hexOrRgb.b) };
}
